import { AssetType } from '../api/models/Shared.types';
import { Recipes, urlWithRecipe } from '../Business/utility/ImageRecipeUtil';
import { Log, Utils } from '@lightningjs/sdk';
import { getDayInfoString, getTime } from '../Business/utility/DateFormatter';
import { DetailDescriptionDataSource } from '../Business/detail/DetailDescriptionDataSource';
import { StreamResolutions } from '../Business/utility/StreamResolutionUtil';
import { FavoriteRequestType } from '../Business/cms/FavoriteRibbonRequest';
import { Theme } from '../config/theme';
import { GuideTranslationKeys } from '../api/models/TranslationKeys';

export function convertDataForMiniDetail(data, backend) {
  // console.log(`ConvertData for type: ${data.type}`, data);
  let result;
  switch (data.type) {
    case AssetType.VOD:
      result = convertVOD(data, backend);
      break;
    case AssetType.SCHEDULE:
      result = convertSCHEDULE(data, backend);
      break;
    case AssetType.CHANNEL:
      result = convertChannel(data, backend);
      break;
    case AssetType.PROGRAM:
      result = convertProgram(data, backend);
      break;
    case AssetType.ARTICLE:
      result = convertArticle(data, backend);
      break;
    case AssetType.SERIES:
      result = convertProgram(data, backend);
      break;
    case AssetType.PAGE:
      result = convertPage(data, backend);
      break;
    case AssetType.BUNDLE:
      result = convertVOD(data, backend);
      break;
    default:
      Log.warn('MiniDetailUtils: unsupported type:', data.type, data);
      result = convertProgram(data, backend);
  }
  // hide title, synopsis and image when program is adult
  if (data.adult) {
    result.title = backend.translations.forKey(GuideTranslationKeys.guide_adult_title);
    result.synopsis = '';
    result.image = Utils.asset('/images/adult.png');
  }
  return result;
}

/**
 * Top level functions
 * **/
function convertArticle(data, backend) {
  let img;
  if (data.detailImage) {
    img = data.detailImage;
  } else {
    img = data.image;
  }
  return {
    title: data.title,
    synopsis: undefined,
    recordingIcons: undefined,
    metaInformation: undefined,
    image: getImageUrl(img, backend, Recipes.DetailPosterRecipe),
  };
}

function convertPage(data, backend) {
  let img;
  if (data.detailImage) {
    img = data.detailImage;
  } else {
    img = data.image;
  }
  return {
    title: data.title,
    synopsis: data.shortSynopsis || data.synopsis,
    recordingIcons: undefined,
    metaInformation: undefined,
    image: getImageUrl(img, backend, Recipes.DetailPosterRecipe),
  };
}

function convertProgram(data, backend) {
  let dataHelper = new DetailDescriptionDataSource(data.id, data.type, backend);
  let metaData;
  if (data.schedule) {
    metaData = getMetaData(backend, data, data.schedule.published, data.season, data.episode, data.genres);
  } else {
    metaData = getMetaData(backend, data, undefined, data.season, data.episode, data.genres);
  }
  let img;
  if (data.detailImage) {
    img = data.detailImage;
  } else {
    img = data.image;
  }
  return {
    title: data.title,
    synopsis: data.synopsis,
    recordingIcons: getIcons({ age: data.minimumAge, resolution: data.resolution }, dataHelper, data),
    metaInformation: metaData,
    image: getImageUrl(img, backend, Recipes.DetailPosterRecipe),
  };
}

function convertChannel(data, backend) {
  if (data.subType === FavoriteRequestType.CHANNEL_LOGO || data.subType === FavoriteRequestType.CHANNEL_TOGGLE) {
    return undefined;
  }
  let img;
  if (data.detailImage) {
    img = data.detailImage;
  } else {
    img = data.image;
  }
  return {
    title: data.title,
    synopsis: undefined,
    recordingIcons: undefined,
    metaInformation: undefined,
    image: getImageUrl(img, backend, Recipes.LandscapeCard.Large),
  };
}

function convertVOD(data, backend) {
  let dataHelper = new DetailDescriptionDataSource(data.id, data.type, backend);
  let img;
  if (data.detailImage) {
    img = data.detailImage;
  } else {
    img = data.image;
  }
  return {
    title: data.title,
    synopsis: data.synopsis,
    recordingIcons: getIcons({ age: data.minimumAge, resolution: data.resolution }, dataHelper, data),
    metaInformation: getMetaInfoVOD(data.productionYear, data.duration, data.genres, backend),
    image: getImageUrl(img, backend, Recipes.DetailPosterRecipe),
  };
}

function convertSCHEDULE(data, backend) {
  let dataHelper = new DetailDescriptionDataSource(data.id, data.type, backend);
  return {
    title: data.title,
    synopsis: data.shortSynopsis,
    recordingIcons: getIcons({ age: data.minimumAge, resolution: data.resolution }, dataHelper, data),
    metaInformation: getMetaData(
      backend,
      data,
      data.schedule.published,
      data.season.season,
      data.season.episode,
      data.genres
    ),
    image: getImageUrl(data.posterImage, backend, Recipes.DetailPosterRecipe),
  };
}

/**
 * Other functions
 * **/

function getMetaInfoVOD(year, duration, genres, backend) {
  let yearStr = '';
  if (year) {
    yearStr = `${year}`;
  }
  let durStr = '';
  if (duration) {
    let dur = Math.floor(duration / 60);
    durStr = `  •  ${dur} min.`;
  }
  let genreStr = getGenres(genres, backend);

  let returnStr = `${yearStr}${durStr}${genreStr}`;
  if (returnStr.length > 1) {
    if (returnStr.startsWith('  •  ')) {
      returnStr = returnStr.substring(5);
    }
    return returnStr;
  } else {
    return undefined;
  }
}

/**\
 * General Functions
 * **/
function getIcons(icons, dataHelper, data) {
  let resIcon = undefined;
  switch (icons.resolution) {
    case 'SD':
      resIcon = dataHelper.getResolutionIcon(StreamResolutions.SD);
      break;
    case 'HD':
      resIcon = dataHelper.getResolutionIcon(StreamResolutions.HD);
      break;
    case '4K':
      resIcon = dataHelper.getResolutionIcon(StreamResolutions.FOURK);
      break;
  }
  // place to add other icons.
  // icons that need to be colored
  let iconArr = [];
  const ageIcon = dataHelper.getAgeratingIcon(icons.age);
  if (ageIcon) {
    iconArr.push({ src: ageIcon, color: Theme.Colors.Primary });
  }
  if (resIcon) {
    iconArr.push({ src: resIcon, color: Theme.Colors.Primary });
  }

  // icons that don't need to be colored
  if (data.recordingIcons) {
    data.recordingIcons.forEach(iconObj => {
      if (iconObj.icon) {
        iconArr.push({ src: iconObj.icon, color: undefined });
      }
    });
  }

  if (data.favoriteIcon) {
    iconArr.push({ src: data.favoriteIcon, color: Theme.Colors.Primary });
  }

  // console.log('Icon arr', iconArr);
  return iconArr;
}

/**
 * @param {Backend} backend
 * @param data
 * @param {IScheduleTime | undefined} schedule
 * @param season
 * @param episode
 * @param genres
 * @return {string}
 */
function getMetaData(backend, data, schedule, season, episode, genres) {
  let genresStr = getGenres(genres, backend);
  let dateStr = '';
  if (schedule) {
    dateStr = getFullTime(schedule.start, schedule.end, backend);
  }

  let dataHelper = new DetailDescriptionDataSource(data.id, data.type, backend);
  let seasonStr = dataHelper.getFormattedSeasonInfo(season, episode);
  if (!seasonStr) {
    seasonStr = '';
  }
  if (seasonStr.length > 1) {
    seasonStr = '  •  ' + seasonStr;
  }
  let returnStr = `${dateStr}${seasonStr}${genresStr}`;
  if (returnStr.startsWith('  •  ')) {
    returnStr = returnStr.substring(5);
  }
  return returnStr;
}

function getImageUrl(url, backend, recipe) {
  if (!url) return undefined;
  if (!url.includes('http')) {
    url = backend.imageURL + url;
  }
  // console.log(url, urlWithRecipe(url, recipe));
  return urlWithRecipe(url, recipe);
}

function getGenres(genres, backend) {
  let genresArray = backend.genres.forGenres(genres);
  let genreStr = '';
  let first = true;
  for (let i = 0; i < genresArray.length; i++) {
    if (first) {
      genreStr += `  •  ${genresArray[i]}`;
      first = false;
    } else {
      genreStr += `, ${genresArray[i]}`;
    }
  }
  return genreStr;
}

function getFullTime(start, end, backend) {
  if (!start || !end) return '';
  let day = getDayInfoString(new Date(start * 1000), true, backend);
  let dateStr = '';

  let first = new Date(start * 1000);
  let second = new Date(end * 1000);

  const time = getTime(first);
  const time2 = getTime(second);
  dateStr += `${day}, ${time} - ${time2}`;
  return dateStr;
}
